import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { KintoneConnectionAccess } from "@/api/kintone-environment";

type InitialSetupSecret =
  | { kind: "standard" }
  | {
      kind: "secure_access";
      clientCert: Uint8Array;
      passphrase: string;
    };

export interface InitialKintoneSetupSessionSummary {
  customerId: string;
  targetSubdomain: string;
  connectionAccessKind: InitialSetupSecret["kind"];
  revision: number;
}

export type InitialKintoneSetupSessionInput =
  | {
      customerId: string;
      targetSubdomain: string;
      connectionAccess: { kind: "standard" };
    }
  | {
      customerId: string;
      targetSubdomain: string;
      connectionAccess: {
        kind: "secure_access";
        clientCert: Uint8Array;
        passphrase: string;
      };
    };

interface StoredSession {
  customerId: string;
  targetSubdomain: string;
  connectionAccess: InitialSetupSecret;
  revision: number;
}

interface InitialKintoneSetupSessionContextValue {
  session: InitialKintoneSetupSessionSummary | null;
  configure: (input: InitialKintoneSetupSessionInput) => void;
  connectionAccessFor: (
    customerId: string,
    targetSubdomain: string,
  ) => KintoneConnectionAccess | null;
  clear: () => void;
}

const InitialKintoneSetupSessionContext =
  createContext<InitialKintoneSetupSessionContextValue | null>(null);

export function InitialKintoneSetupSessionProvider({
  activeCustomerId,
  children,
  initialSession,
}: {
  activeCustomerId: string;
  children: ReactNode;
  initialSession?: InitialKintoneSetupSessionInput;
}) {
  const initial = useMemo(
    () => initialSession ? storeSession(initialSession, 1) : null,
    [],
  );
  const sessionRef = useRef<StoredSession | null>(initial);
  const [session, setSession] = useState<InitialKintoneSetupSessionSummary | null>(
    initial ? summarize(initial) : null,
  );

  const clear = useCallback(() => {
    destroyStoredSession(sessionRef.current);
    sessionRef.current = null;
    setSession(null);
  }, []);

  const configure = useCallback((input: InitialKintoneSetupSessionInput) => {
    const nextRevision = (sessionRef.current?.revision ?? 0) + 1;
    destroyStoredSession(sessionRef.current);
    const next = storeSession(input, nextRevision);
    sessionRef.current = next;
    setSession(summarize(next));
  }, []);

  const connectionAccessFor = useCallback((
    customerId: string,
    targetSubdomain: string,
  ): KintoneConnectionAccess | null => {
    const current = sessionRef.current;
    if (!current || current.customerId !== customerId ||
        current.targetSubdomain !== targetSubdomain) {
      return null;
    }
    if (current.connectionAccess.kind === "standard") {
      return { kind: "standard" };
    }
    return {
      kind: "secure_access",
      clientCertBase64: bytesToBase64(current.connectionAccess.clientCert),
      passphrase: current.connectionAccess.passphrase,
    };
  }, []);

  useEffect(() => {
    if (sessionRef.current &&
        sessionRef.current.customerId !== activeCustomerId) {
      clear();
    }
  }, [activeCustomerId, clear]);

  useEffect(() => () => {
    destroyStoredSession(sessionRef.current);
    sessionRef.current = null;
  }, []);

  return (
    <InitialKintoneSetupSessionContext.Provider
      value={{ session, configure, connectionAccessFor, clear }}
    >
      {children}
    </InitialKintoneSetupSessionContext.Provider>
  );
}

export function useInitialKintoneSetupSession() {
  const value = useContext(InitialKintoneSetupSessionContext);
  if (!value) {
    throw new Error("initial_kintone_setup_session_provider_missing");
  }
  return value;
}

function storeSession(
  input: InitialKintoneSetupSessionInput,
  revision: number,
): StoredSession {
  if (input.connectionAccess.kind === "secure_access" &&
      input.connectionAccess.passphrase.trim().length === 0) {
    throw new Error("initial_setup_secure_access_passphrase_required");
  }
  return {
    customerId: input.customerId,
    targetSubdomain: input.targetSubdomain,
    connectionAccess: input.connectionAccess.kind === "standard"
      ? { kind: "standard" }
      : {
          kind: "secure_access",
          clientCert: Uint8Array.from(input.connectionAccess.clientCert),
          passphrase: input.connectionAccess.passphrase,
        },
    revision,
  };
}

function summarize(session: StoredSession): InitialKintoneSetupSessionSummary {
  return {
    customerId: session.customerId,
    targetSubdomain: session.targetSubdomain,
    connectionAccessKind: session.connectionAccess.kind,
    revision: session.revision,
  };
}

function destroyStoredSession(session: StoredSession | null) {
  if (session?.connectionAccess.kind === "secure_access") {
    session.connectionAccess.clientCert.fill(0);
    session.connectionAccess.passphrase = "";
  }
}

function bytesToBase64(bytes: Uint8Array) {
  let binary = "";
  const chunkSize = 32_768;
  for (let offset = 0; offset < bytes.byteLength; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}
